/**
 * THE DATA PROGRAMME — what actually happens to a client's data, end to end.
 * Built 2026-08-28 for Outform, but deliberately written as the GENERIC shape:
 * every client we take on with a spreadsheet estate runs this same pipeline.
 *
 * The question it answers: "you keep saying data normalisation — show me."
 *
 * A DATA-FLOW view on the house engine (`./c4`), so the numbered discs on the
 * wires ARE the diagram. Strip the numbers and it collapses into a row of
 * boxes, which is the thing `skill://diagrams` exists to prevent. Every stage
 * is a TOP-LEVEL column rather than a nested band, for the reason recorded in
 * that skill on 2026-08-06: c4 paints the wire SVG UNDER the boxes, so a wire
 * routed inside a container is invisible.
 *
 * ── THE ARGUMENT THE LAYOUT IS MAKING ──────────────────────────────────────
 *
 * 1 · FIVE SOURCES, NOT ONE. Column one is the correction that cost us most
 *     this week. Everything we measured for Outform — 51 files, 278 tabs,
 *     370,136 cells, the 13% record figure, the mastering curve — is the
 *     ESTIMATING WORKBOOKS alone. Their own v4 prototype
 *     (drop/Outform-Estimate-Template-v4_1.html) names five: workbooks, CRM,
 *     ERP, a debrief log and dated external feeds. Three of them we have never
 *     seen, and one of those three holds the win/loss data that their §3.7.1
 *     weights at 40%. A single-source cleanup drawn as a five-source pipeline
 *     is the honest picture, and the red boxes say which is which.
 *
 * 2 · ONE HUMAN STEP. Column four is the only place a person appears, and it
 *     is deliberately narrow: a machine profiles and proposes, an ESTIMATOR
 *     adjudicates, the machine publishes and enforces. Programmes die in that
 *     queue, so it gets its own column rather than a footnote.
 *
 * 3 · THE LOOP IS NOT A SECOND PIPELINE. The dashed return wire is the whole
 *     answer to "how do we keep it up to date": a new job in 2027 walks the
 *     same five stages as a folder from 2024. There is no separate mechanism,
 *     which is why there is no separate row.
 *
 * 4 · TIME IS A FIRST-CLASS COLUMN. `SCD Type 2` sits in MODEL because their
 *     labour rates are dated 2021, their tariff percentage moves, and their
 *     cost model changed between 2024 and 2025. Microsoft's own phrasing for
 *     why this matters is on the tin: the primary role of a warehouse is "to
 *     describe the past accurately".
 *
 * Nothing here has a hand-written width. A leaf measures its own text, a
 * container measures its children, and every wire resolves against the placed
 * rects — so adding a source or a stage cannot break the geometry.
 */
import { Canvas, leaf, person, box, type Diagram } from "./c4";

const PROGRAMME: Diagram = (() => {
  // ── WHO IT IS FOR ──────────────────────────────────────────────────────────
  // Drawn bare, outside every boundary. The estimator is the only human in the
  // pipeline and the only human it is built for — the same person twice, which
  // is the point of putting them at the far left rather than in a box.
  const actor = box("dp-act", undefined, "col", [person("dp-est", "Estimator")], { pad: 0 });

  // ── ① SOURCES ──────────────────────────────────────────────────────────────
  // Red = we have never had access. Not a criticism of anyone; it is the gap
  // that decides how much of their own blueprint is currently buildable.
  const sources = box("dp-src", "① SOURCE SYSTEMS — five, not one", "col", [
    leaf("dp-wb", "Estimating workbooks", "[1,487 jobs · audited]", "FileSpreadsheet"),
    leaf("dp-crm", "CRM — win/loss", "[351 quotes · NO ACCESS]", "Handshake", "gate"),
    leaf("dp-erp", "D365 ERP", "[POs · actuals · NO ACCESS]", "Building2", "gate"),
    leaf("dp-deb", "Debrief log", "[41 competitor prices]", "MessagesSquare", "gate"),
    leaf("dp-feed", "Freight · tariff", "[dated · expires]", "Ship"),
  ], { gap: 12 });

  // ── ② LAND ─────────────────────────────────────────────────────────────────
  // Bronze. Databricks' own rule: minimal validation, fields kept as string or
  // VARIANT, "to protect against unexpected schema changes". We would have
  // typed everything at the door and lost rows at folder 900.
  const land = box("dp-land", "② LAND — raw, never mutated", "col", [
    leaf("dp-obj", "Object store", "[SHA-256 · immutable]", "Archive"),
    leaf("dp-man", "Manifest", "[file · sheet · cell]", "ListTree"),
    leaf("dp-untyped", "Untyped on arrival", "[survives schema drift]", "ShieldCheck"),
  ], { gap: 12 });

  // ── ③ EXTRACT & PROFILE ────────────────────────────────────────────────────
  // Everything loads. Nothing waits for a human. Uncertainty is a COLUMN.
  const extract = box("dp-ext", "③ EXTRACT & PROFILE — everything loads", "col", [
    leaf("dp-walk", "Marker-walk parser", "[labels, never cells]", "ScanSearch"),
    leaf("dp-prof", "Profiler", "[nulls · distincts · patterns]", "BarChart3"),
    leaf("dp-dq", "Quality rules", "[6 named DQ dimensions]", "BadgeCheck"),
    leaf("dp-orac", "Reconciliation oracle", "[Estimate ↔ Breakout]", "Scale"),
    leaf("dp-stat", "Status per row", "[confirmed · review · conflict]", "Flag"),
  ], { gap: 12 });

  // ── ④ MASTER ───────────────────────────────────────────────────────────────
  // The only human step, and the one that decides whether the programme ships.
  const master = box("dp-mas", "④ MASTER — the only human step", "col", [
    leaf("dp-match", "Match & merge", "[confidence score]", "GitMerge"),
    leaf("dp-queue", "Steward queue", "[an ESTIMATOR decides]", "UserCheck", "gate"),
    leaf("dp-xref", "xref crosswalk", "[every spelling → one id]", "Link"),
    leaf("dp-sor", "System of record", "[which source is master]", "Crown"),
  ], { gap: 12 });

  // ── ⑤ MODEL ────────────────────────────────────────────────────────────────
  // Conformed dimensions is the technique multi-source demands and single-source
  // does not: a client in the CRM and LO'REAL in a workbook must be one row, or
  // a win can never be joined to a cost.
  const model = box("dp-mod", "⑤ MODEL — one shape, across all five", "col", [
    leaf("dp-conf", "Conformed dimensions", "[one client, every source]", "Combine"),
    leaf("dp-scd", "SCD Type 2", "[describe the past]", "History"),
    leaf("dp-sk", "Surrogate keys", "[their number kept]", "KeyRound"),
    leaf("dp-star", "Star schema", "[facts + dimensions]", "Star"),
    leaf("dp-grain", "Grain declared", "[one row per what]", "Ruler"),
  ], { gap: 12 });

  // ── ⑥ SERVE ────────────────────────────────────────────────────────────────
  // Three retrieval paths, because embeddings cannot aggregate. Structure what
  // you filter or sum; embed only what you compare by resemblance.
  const serve = box("dp-srv", "⑥ SERVE — three ways in", "col", [
    leaf("dp-sql", "SQL", "[filter · sum · group]", "Table2"),
    leaf("dp-vec", "Vector search", "[resemblance only]", "Radar"),
    leaf("dp-doc", "Document fetch", "[the original file]", "FileSearch"),
    leaf("dp-agent", "The agent", "[provenance per number]", "Sparkles"),
  ], { gap: 12 });

  return {
    key: "dp",
    title: "The data programme — source systems to a queryable history",
    subtitle:
      "Six stages, one human step, and the loop that keeps it current · red = a source we have not been given · 2026-08-28",
    padBottom: 118,
    cols: [
      { root: actor, align: "center" },
      { root: sources, align: "center" },
      { root: land, align: "center" },
      { root: extract, align: "center" },
      { root: master, align: "center" },
      { root: model, align: "center" },
      { root: serve, align: "center" },
    ],
    wires: [
      // THE MAIN LINE. Numbers are the diagram; labels are kept to one or two
      // words because a label whose midpoint lands on a box gets clipped
      // mid-word (skill://diagrams, 2026-08-06).
      { a: "dp-src", b: "dp-land", as: "r", bs: "l", num: 1, label: "copy" },
      { a: "dp-land", b: "dp-ext", as: "r", bs: "l", num: 2, label: "parse" },
      { a: "dp-ext", b: "dp-mas", as: "r", bs: "l", num: 3, label: "propose" },
      { a: "dp-mas", b: "dp-mod", as: "r", bs: "l", num: 4, label: "publish" },
      { a: "dp-mod", b: "dp-srv", as: "r", bs: "l", num: 5, label: "query" },

      // THE HUMAN. One wire, one person, one decision — drawn over the top so
      // it clears every container between the estimator and the queue.
      { a: "dp-est", b: "dp-queue", as: "t", bs: "t", label: "adjudicates" },

      // THE LOOP — routed underneath so it crosses no container fill. This is
      // the answer to "how do we keep it up to date": there is no second
      // mechanism, so there is no second row.
      { a: "dp-srv", b: "dp-src", as: "b", bs: "b", dashed: true, label: "every new job" },

      // Exceptions return to the steward rather than being silently guessed.
      { a: "dp-stat", b: "dp-queue", as: "b", bs: "b", dashed: true },

      // The three sources we do not have. Deliberately unlabelled — all three
      // midpoints land on a container edge, and the legend has room for the
      // sentence that matters.
      { a: "dp-crm", b: "dp-land", as: "t", bs: "t", red: true },
    ],
    legend: [
      { style: "solid", label: "one pass — everything loads, nothing waits for a human, uncertainty is a column not an exclusion" },
      { style: "dash", label: "the loop — an unmatched value returns to the steward; a job from 2027 walks the same five stages as a folder from 2024" },
      { style: "red", label: "not in our hands yet: the CRM holds the win/loss their own scoring model weights at 40%, the ERP holds actual cost, and 41 debriefs hold competitor prices" },
    ],
  };
})();

/** Source systems → a queryable history. The generic shape, drawn on Outform. */
export function DataProgrammeDiagram() {
  return <Canvas d={PROGRAMME} mode="contrast" />;
}
